<template>
  <div class="announcement-page">
    <!-- skeleton loader — announcement-shaped silhouette (header with
         puck + title + time, meta chip row, three body lines) so the
         page lands without layout shift once the real content arrives.
         back button stays live so the user can still bail out. -->
    <div v-if="loading" class="announcement-content" aria-busy="true" aria-label="Loading announcement">
      <div class="announcement-header">
        <button type="button" class="announcement-back-btn" @click="goBack" aria-label="Back">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="announcement-puck announcement-puck-skeleton"></span>
        <div class="announcement-title-block">
          <span class="ad-skeleton ad-skeleton-title"></span>
          <span class="ad-skeleton ad-skeleton-time"></span>
        </div>
      </div>
      <div class="announcement-meta">
        <span class="ad-skeleton ad-skeleton-chip"></span>
        <span class="ad-skeleton ad-skeleton-chip"></span>
        <span class="ad-skeleton ad-skeleton-chip ad-skeleton-chip--wide"></span>
      </div>
      <div class="announcement-body">
        <span class="ad-skeleton ad-skeleton-line"></span>
        <span class="ad-skeleton ad-skeleton-line ad-skeleton-line--short"></span>
        <span class="ad-skeleton ad-skeleton-line"></span>
      </div>
    </div>
    <div v-else-if="notFound" class="announcement-notfound">
      <i class="fa fa-bullhorn" aria-hidden="true"></i>
      <div class="announcement-notfound-title">Announcement not available</div>
      <div class="announcement-notfound-sub">
        This announcement may have expired, been removed, or wasn't addressed to you.
      </div>
      <b-button size="sm" variant="outline-secondary" @click="goBack">Back</b-button>
    </div>
    <div v-else-if="announcement" class="announcement-content">
      <!-- flat list-page-style header: circular back button + left-aligned
           title on the first row, then a meta chip row underneath. no
           card chrome — the page shell owns the layout. -->
      <div class="announcement-header">
        <button type="button" class="announcement-back-btn" @click="goBack" aria-label="Back">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <!-- elevated type puck: same icon/color system as the popup + list
             pucks so this page reads as part of the same family, scaled up
             and given a soft drop-shadow so it feels like the page's
             headline visual rather than an inline chip. -->
        <span class="announcement-puck" :class="'announcement-puck-' + puckStyle">
          <span v-if="_isSvgIconWrap(puckIcon)" class="ni-svg" v-html="_svgIconHtmlWrap(puckIcon)" aria-hidden="true"></span>
          <i v-else class="fa" :class="puckIcon" aria-hidden="true"></i>
        </span>
        <div class="announcement-title-block">
          <h1 class="announcement-title">{{ announcement.title }}</h1>
          <div class="announcement-header-time">{{ formattedTime }}</div>
        </div>
      </div>
      <div class="announcement-meta">
        <span v-if="kindBadgeLabel" class="announcement-badge" :class="kindBadgeClass">
          <span v-if="_isSvgIconWrap(kindBadgeIcon)" class="ni-svg" v-html="_svgIconHtmlWrap(kindBadgeIcon)" aria-hidden="true"></span>
          <i v-else class="fa" :class="kindBadgeIcon" aria-hidden="true"></i>
          {{ kindBadgeLabel }}
        </span>
        <!-- target-tenant + target-role badges: BOTH staff-only
             (effectiveRole === 'admin'). muggles never see either,
             muggle preview hides them too. role badge omits itself
             when the announcement targets every role (no narrowing
             to surface). -->
        <span v-if="effectiveRole === 'admin'" class="announcement-badge" :class="targetBadgeClass">
          <i class="fa" :class="announcement.target === 'tenant' ? 'fa-building-o' : 'fa-globe'" aria-hidden="true"></i>
          {{ targetLabel }}
        </span>
        <span v-if="showTargetRole" class="announcement-badge announcement-badge-roles" :title="targetRoleLabel">
          <i class="fa fa-users" aria-hidden="true"></i>
          {{ targetRoleLabel }}
        </span>
      </div>
      <!-- body rendering branches on body_format. markdown rows run
           through the shared help-article renderer (v-html is safe —
           the renderer escapes untrusted input). html rows get a
           sandboxed iframe via srcdoc so the author's <style> and
           classes can't leak into the host page. matches the pattern
           used in help_center.vue's read-only detail pane. -->
      <iframe
        v-if="isHtmlBody"
        ref="htmlBody"
        class="announcement-body announcement-body-iframe"
        :srcdoc="bodySrcdoc"
        title="Announcement"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        @load="_resizeBodyIframe"
      ></iframe>
      <div
        v-else
        class="markdown-content markdown-page announcement-body"
        v-html="renderedBody"
      ></div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'
import { get_user_announcement } from '../networking/apis'
import { tbn_get_user_announcement } from '../networking/tbn_apis'
import { iconForAnnouncement, isSvgIcon, customSvgHtml } from '../utils/notification_icons'

export default {
  name: 'AnnouncementDetail',
  data() {
    return {
      loading: true,
      notFound: false,
      announcement: null,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenantType() {
      if (!this.user || !this.user.tenant) return 'CM'
      return (this.user.types && this.user.types[this.user.tenant]) || 'CM'
    },
    isTbn() {
      return this.tenantType === 'TBN'
    },
    announcementId() {
      return this.$route.params && this.$route.params.announcement_id
    },
    // true when the loaded announcement row is an html-format row.
    // drives the template branch between markdown v-html and iframe
    // srcdoc — html bodies (apple-style marketing announcements) carry
    // their own <style> + layout and must be isolated from the host.
    isHtmlBody() {
      return !!(this.announcement && this.announcement.body_format === 'html')
    },
    renderedBody() {
      // markdown path only. html rows bypass this via bodySrcdoc.
      const md = this.announcement && this.announcement.body_markdown
      if (!md) return ''
      try { return renderMarkdownToHtml(md) } catch (_) { return md }
    },
    // srcdoc for the html iframe — wraps the author's html in a minimal
    // doctype + base target="_blank" so links open in a new tab and
    // their own CSS resolves naturally. mirrors help_center.vue's
    // read-only detail pane; keep the wrappers in sync.
    bodySrcdoc() {
      if (!this.isHtmlBody) return ''
      const html = (this.announcement && this.announcement.body_markdown) || ''
      return this._wrapAnnouncementHtml(html)
    },
    targetLabel() {
      if (!this.announcement) return ''
      if (this.announcement.target === 'tenant') {
        const tid = this.announcement.tenant_id
        const opts = (this.user && this.user.tenant_options) || []
        const match = opts.find(t => (t && (t.value || t.tenant_id)) === tid)
        if (match) return match.text || match.name || match.value || 'Specific customer'
        return tid ? ('Customer: ' + tid) : 'Specific customer'
      }
      return 'All customers'
    },
    targetBadgeClass() {
      if (!this.announcement) return 'announcement-badge-global'
      return this.announcement.target === 'tenant'
        ? 'announcement-badge-tenant'
        : 'announcement-badge-global'
    },
    // staff-only target badges (target-tenant + target-role). drives off
    // effectiveRole so the Muggle preview toggle hides them too. real
    // non-admins never see either. role badge also self-suppresses when
    // the announcement targets every role.
    effectiveRole() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
      return this.user && this.user.role
    },
    targetRoleLabel() {
      const a = this.announcement || {}
      const roles = a.roles || []
      if (!roles.length) return ''
      // friendly labels mirror help_center.vue announcementRoleOptions.
      // unknown values fall through to the raw token so legacy rows
      // still render something.
      const map = {
        'admin': 'Sys Admin',
        'customer-admin': 'Fleet Admin',
        'customer': 'Staff',
        'manager': 'Manager',
        'dispatch': 'Dispatch',
        'driver': 'Driver',
        'garage': 'Garage',
        'wash-bay': 'Wash Bay',
        'customer-care': 'Customer Care',
        'booking-customer': 'Booking Customer',
      }
      return roles.map(r => map[r] || r).join(', ')
    },
    showTargetRole() {
      return this.effectiveRole === 'admin' && !!this.targetRoleLabel
    },
    isServiceAlert() {
      return !!(this.announcement && this.announcement.kind === 'service_alert')
    },
    isServiceResolved() {
      return !!(this.announcement && this.announcement.kind === 'service_resolved')
    },
    puckStyle() {
      if (this.isServiceAlert) return 'service-alert'
      if (this.isServiceResolved) return 'service-resolved'
      return 'announcement'
    },
    puckIcon() {
      // prefers the per-row icon override (set in the announcement
      // composer); falls back to the kind default. puckStyle still
      // tracks `kind` so the puck color stays consistent with urgency.
      return iconForAnnouncement(this.announcement || {})
    },
    // kind badge — only renders for non-default kinds. announcement is the
    // baseline so it doesn't need a chip; service_alert renders amber and
    // service_resolved renders green so the page identity is obvious.
    kindBadgeLabel() {
      if (this.isServiceAlert) return 'Service alert'
      if (this.isServiceResolved) return 'Service resolved'
      return ''
    },
    kindBadgeClass() {
      if (this.isServiceAlert) return 'announcement-badge-service-alert'
      if (this.isServiceResolved) return 'announcement-badge-service-resolved'
      return ''
    },
    kindBadgeIcon() {
      if (this.isServiceAlert) return 'fa-exclamation-triangle'
      if (this.isServiceResolved) return 'fa-check-circle'
      return ''
    },
    // shows the announcement's effective time — published_at when it
    // went out, created_at while it's still a draft. matches the
    // notifications list (which renders fan-out time) so an admin
    // sharing a link sees the same timestamp the recipient does.
    formattedTime() {
      const a = this.announcement || {}
      const iso = a.published_at || a.created_at
      if (!iso) return ''
      try {
        return moment(iso).format('MMMM D, YYYY [at] h:mm A')
      } catch (_) { return iso }
    },
  },
  created() {
    this.load()
  },
  watch: {
    announcementId() { this.load() },
  },
  methods: {
    _isSvgIconWrap(name) { return isSvgIcon(name) },
    _svgIconHtmlWrap(name) { return customSvgHtml(name) },
    async load() {
      if (!this.announcementId) {
        this.notFound = true
        this.loading = false
        return
      }
      this.loading = true
      this.notFound = false
      this.announcement = null
      try {
        const fn = this.isTbn ? tbn_get_user_announcement : get_user_announcement
        const resp = await fn(this.user.tenant, this.announcementId)
        const axiosData = (resp && resp.value && resp.value.data) || {}
        const payload = axiosData.data || axiosData
        if (payload && payload.announcement_id) {
          this.announcement = payload
        } else {
          this.notFound = true
        }
      } catch (_) {
        this.notFound = true
      } finally {
        this.loading = false
      }
    },
    goBack() {
      try {
        if (window.history.length > 1) this.$router.go(-1)
        else this.$router.push({ name: 'dashboard' })
      } catch (_) {
        this.$router.push('/')
      }
    },
    // wrap author-supplied html in a minimal document so their own CSS
    // resolves naturally inside the iframe, and force link navigation
    // out via <base target="_blank">. duplicated verbatim from the
    // help_center.vue composer; keep in sync (candidate to extract to
    // a shared helper once the pattern stabilizes).
    _wrapAnnouncementHtml(html) {
      const safe = html || ''
      return '<!doctype html>' +
        '<html lang="en"><head><meta charset="utf-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1">' +
        '<base target="_blank">' +
        '<style>' +
          'html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1d1d1f;background:#fff;line-height:1.5;}' +
          'body{padding:24px;}' +
          'img{max-width:100%;height:auto;}' +
          'a{color:#0071e3;}' +
        '</style></head><body>' + safe + '</body></html>'
    },
    // size the iframe to its content on load. the announcement page
    // is a regular scrolling page (unlike the modal preview), so we
    // want the iframe to grow to fit rather than inner-scroll. mirrors
    // help_center.vue's _resizeAnnouncementIframe — images and webfonts
    // land after the iframe's own load event, so we re-measure on each
    // image load and after a short settle delay. otherwise the frame
    // freezes at its pre-image height and the body scrolls internally.
    _resizeBodyIframe(ev) {
      const iframe = (ev && ev.target) || this.$refs.htmlBody
      if (!iframe || !iframe.contentDocument) return
      const doc = iframe.contentDocument
      const setHeight = () => {
        try {
          const h = Math.max(
            doc.body ? doc.body.scrollHeight : 0,
            doc.documentElement ? doc.documentElement.scrollHeight : 0,
          )
          if (h > 0) iframe.style.height = (h + 12) + 'px'
        } catch (_) {}
      }
      setHeight()
      try {
        const imgs = doc.images || []
        for (let i = 0; i < imgs.length; i++) {
          if (!imgs[i].complete) imgs[i].addEventListener('load', setHeight, { once: true })
        }
      } catch (_) {}
      setTimeout(setHeight, 200)
      this._installAnnouncementActionHandler(doc)
    },
    // announcement HTML authors can't run inline scripts (sandbox lacks
    // allow-scripts), so any CTA that wants to do more than "open a new
    // tab" uses a special href scheme the parent intercepts here:
    //   #fi-action:fa-tutorial           → open Fleet Assistant with tutorial
    //   #fi-action:fa-tutorial:<message> → same, but pre-fill the input
    //   #fi-action:nav:/some/path        → router.push (stays in same window)
    // everything else falls through to <base target=_blank>. mirrors the
    // preview-modal handler in help_center.vue; keep them in sync.
    // without this, fi-action hrefs escape via <base target="_blank">
    // and open at web.fleet-integrations.com#fi-action:… — the app's
    // hash router can't match and the user lands on a blank page.
    _installAnnouncementActionHandler(doc) {
      if (!doc || doc.__fiActionHandlerInstalled) return
      doc.__fiActionHandlerInstalled = true
      // Safari gotcha: <base target="_blank"> in the srcdoc wrapper makes
      // every anchor inherit target=_blank, and Safari decides to open a
      // new tab synchronously at mousedown — before our capture-phase
      // click handler can preventDefault(). result: a stray new tab
      // opens at #/fi-action:fa-tutorial:… which the hash router can't
      // match. override target on fi-action anchors so Safari sees them
      // as same-tab and our handler gets to intercept cleanly.
      try {
        const anchors = doc.querySelectorAll('a[href^="#fi-action:"]')
        for (let i = 0; i < anchors.length; i++) anchors[i].setAttribute('target', '_self')
      } catch (_) {}
      doc.addEventListener('click', (e) => {
        let el = e.target
        while (el && el !== doc.body && (!el.tagName || el.tagName.toLowerCase() !== 'a')) {
          el = el.parentNode
        }
        if (!el || !el.tagName || el.tagName.toLowerCase() !== 'a') return
        const href = el.getAttribute('href') || ''
        const m = href.match(/^#fi-action:([a-z-]+)(?::(.*))?$/i)
        if (!m) return  // ordinary link: let <base target=_blank> open a tab
        e.preventDefault()
        const action = m[1]
        const arg = m[2] || ''
        if (action === 'fa-tutorial') {
          this.$root.$emit('open-fleet-assistant-tutorial', {
            message: arg || 'Show me what you can do.'
          })
        } else if (action === 'nav') {
          // defense-in-depth: reject anything that isn't a same-origin
          // router path (rejects //evil.com, http://…, javascript:, bare).
          if (typeof arg !== 'string' || !arg.startsWith('/')) return
          try { this.$router.push(arg) } catch (_) {}
        }
      }, true)
    },
  },
}
</script>

<style scoped>
.announcement-page {
  max-width: 960px;
  margin: 20px auto 40px;
  padding: 0 16px;
  text-align: left;
}

/* iframe rendering for html-format announcements. width fills the page
   column; height is set by _resizeBodyIframe on load so the document
   flows naturally (no inner scroll). kept minimal — the srcdoc wrapper
   supplies typography/background. */
.announcement-body-iframe {
  display: block;
  width: 100%;
  border: 0;
  background: #fff;
  min-height: 320px;
}

.announcement-notfound {
  text-align: center;
  padding: 48px 20px;
  color: #6c757d;
}

/* skeleton loader — same shimmer recipe as support_ticket_details so
   both detail pages read as one family. primitives below are sized to
   match the real elements they stand in for so the layout doesn't
   jump when the real content lands. */
.ad-skeleton {
  display: inline-block;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: ad-skeleton-shimmer 1.4s ease-in-out infinite;
  line-height: 1;
}

@keyframes ad-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.announcement-puck-skeleton {
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  border-color: #dee2e6;
  animation: ad-skeleton-shimmer 1.4s ease-in-out infinite;
  box-shadow: none;
}

/* mirror the real stacked title-block: heights match the real
   title line box (22px font * 1.3 line-height = 29px) and time
   (12px font * 1.5 = 18px). block height matches → puck centers
   the same way under align-items:center, no vertical drift. */
.ad-skeleton-title {
  width: 320px;
  max-width: 100%;
  height: 28px;
  border-radius: 4px;
}

.ad-skeleton-time {
  width: 120px;
  height: 16px;
}

.ad-skeleton-chip {
  width: 92px;
  height: 20px;
  border-radius: 4px;
}

.ad-skeleton-chip--wide {
  width: 190px;
}

.ad-skeleton-line {
  display: block;
  height: 14px;
  width: 100%;
  margin-bottom: 10px;
}

.ad-skeleton-line--short {
  width: 70%;
}

.announcement-notfound .fa {
  display: block;
  font-size: 36px;
  margin-bottom: 10px;
  color: #cdd6e0;
}

.announcement-notfound-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
}

.announcement-notfound-sub {
  font-size: 13px;
  margin-bottom: 14px;
}

/* flat page header — matches the notifications list shell. puck + title
   + time sit flush with the content column; the circular back button
   floats into the left page margin via absolute positioning so it sits
   outside the content's left edge (falls back to inline on narrow
   viewports where there is no margin to float into). */
.announcement-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 0 12px;
  border-bottom: 1px solid #edf0f3;
  margin-bottom: 14px;
}

/* circular back — same spec as std-back-btn on support-ticket detail.
   absolute-positioned 46px to the left of the header (34px btn + 12px
   breathing room) so it lives in the page margin. top offset = (puck
   height - btn height) / 2 = (48 - 34) / 2 = 7px, keeping it visually
   centered with the puck. */
.announcement-back-btn {
  position: absolute;
  left: -46px;
  top: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #d4d8dc;
  background: #fff;
  color: #495057;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.announcement-back-btn:hover,
.announcement-back-btn:focus {
  background: #f0f5ff;
  border-color: #4a7ba7;
  color: #1f4a73;
  outline: none;
}
.announcement-back-btn .fa { line-height: 1; }

/* below ~1060px there isn't enough page margin to float the back button
   outside the content, so re-flow it inline as the first header item. */
@media (max-width: 1060px) {
  .announcement-back-btn {
    position: static;
    top: auto;
    left: auto;
  }
}

/* elevated type puck — same outline-box pattern as the popup/list pucks
   (pale tint, colored 2px border, colored icon) but upscaled to 48px
   with a soft layered drop-shadow so it reads as the page's headline
   visual. per-type color tokens override the defaults. */
.announcement-puck {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  /* softened outline: border sits closer to the tint so the puck reads
     as a cohesive shape rather than an outlined chip. icon + tinted
     halo still carry the type identity. matches the read-state puck
     style in the popup notifications list. */
  border: 2px solid #cbd5e1;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  box-sizing: border-box;
  box-shadow:
    0 2px 5px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.05);
}

.announcement-puck-announcement {
  background: #f3f0ff;
  border-color: #d8cffb;
  color: #7c5dd0;
  box-shadow:
    0 2px 5px rgba(124, 93, 208, 0.16),
    0 1px 2px rgba(124, 93, 208, 0.08);
}

.announcement-puck-service-alert {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
  box-shadow:
    0 2px 5px rgba(217, 119, 6, 0.16),
    0 1px 2px rgba(217, 119, 6, 0.08);
}

.announcement-puck-service-resolved {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
  box-shadow:
    0 2px 5px rgba(5, 150, 105, 0.16),
    0 1px 2px rgba(5, 150, 105, 0.08);
}

/* title + time stacked column — the block owns the flex sizing in
   the header row so the title wraps inside its column and the time
   sits directly beneath as a muted subtitle. */
.announcement-title-block {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.announcement-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.3;
  text-align: left;
  word-break: break-word;
}

.announcement-header-time {
  color: #8a8a8a;
  font-size: 12px;
  font-style: italic;
}

.announcement-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  margin-bottom: 18px;
}

.announcement-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
}

.announcement-badge .fa { font-size: 10px; }

.announcement-badge-global {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  color: #0b3d91;
}

.announcement-badge-tenant {
  --badge-color: #6f42c1;
  --badge-bg: #f1ebff;
  color: #4b2c92;
}

.announcement-badge-author {
  --badge-color: #6c757d;
  --badge-bg: #f1f3f5;
  color: #495057;
}

/* service-alert badge: amber to signal urgency without alarm-red. matches
   the puck/chip palette used in the notification list and popup. */
.announcement-badge-service-alert {
  --badge-color: #f0a957;
  --badge-bg: #fef3d5;
  color: #8a5a00;
}

.announcement-badge-service-resolved {
  --badge-color: #059669;
  --badge-bg: #d1fae5;
  color: #065f46;
}

.announcement-body {
  padding: 4px 0 8px;
}
</style>
